import { selectCurrentEntity } from '@opengov/app-core/entity/selectors';
import { createSelector } from '@opengov/compose-app/reselect';
import { selectIsQueryRunning } from '@opengov/ogql-client/selectors';
import { customColorsToHexValueMap } from '@opengov/reporting-transforms/visualizations/chartCalculations';
import {
  EAllLedgerType,
  EQueryType,
  IExpandedGroupKeys,
  IExpandedRows,
  IReportConfiguration,
  IRowSource,
  isMultiHierarchyAccountSummary,
  isMultiHierarchyRowOverride,
  isRowDataSource,
  isRowOverride,
  StackingOptions,
  type MultiHierarchyCompositeFilter
} from '@opengov/reporting-types';

import { selectCurrentPathname } from '../../../module/routes/paths';
import { selectOrganizeColumnsByAccountTypeFlag } from '../flags';
import { selectPathname } from '../report/selectors/pathnameSelectors';
import { reportConfigurationSelector } from './state';

export const selectCurrentReportConfiguration = createSelector(
  reportConfigurationSelector,
  (state) => state.currentReportConfiguration
);

export const selectPresentationConfiguration = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) => reportConfig && reportConfig.presentationConfiguration
);

export const selectActiveReportTableTab = createSelector(selectPresentationConfiguration, (presentationConfig) => {
  if (!presentationConfig) {
    return undefined;
  }
  return presentationConfig.reportTableActiveTab;
});

export const selectActiveTableSort = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) =>
    reportConfig && {
      sortColumn: reportConfig.detailTableQuery.sortColumn,
      sortDirection: reportConfig.detailTableQuery.sortDirection
    }
);

export const selectChartType = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) => reportConfig && reportConfig.visualizationQuery.chartType
);

export const selectColumnNames = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) => reportConfig && reportConfig.presentationConfiguration.columnNames
);

export const selectColumnGroups = createSelector(
  selectCurrentReportConfiguration,
  (state) => state?.accountSummary?.columnGroups || []
);

export const selectCustomColors = createSelector(
  selectPresentationConfiguration,
  (presentationConfig) => presentationConfig && presentationConfig.colors
);

export const selectCustomColorMap = createSelector(selectCustomColors, (customColors) =>
  customColors ? customColorsToHexValueMap(customColors as any) : {}
);

export const selectCurrentFilters = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) => reportConfig && reportConfig.visualizationQuery.filter
);

export const selectDetailTableFilter = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) => reportConfig && reportConfig.detailTableQuery.filter
);

export const selectDetailTableFilterBooleanOperators = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) => reportConfig && reportConfig.detailTableQuery.filterBooleanOperators
);

export const selectDetailTableGroupByColumns = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) => reportConfig?.detailTableQuery.groupByColumns
);

export const selectDetailTableShowTotals = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) => reportConfig?.detailTableQuery.showTotals
);

export const selectDimensionalGlobalSegmentFilters = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) =>
    reportConfig &&
    reportConfig.dimensionalSummaryQueries[0] &&
    reportConfig.dimensionalSummaryQueries[0].globalSegmentFilters
);

export const selectDimensionalSegmentFilters = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) =>
    reportConfig &&
    reportConfig.dimensionalSummaryQueries[0] &&
    reportConfig.dimensionalSummaryQueries[0].segmentFilters
);

export const selectDimensionalSummaryBlockNames = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) => reportConfig && reportConfig.presentationConfiguration.dimensionalSummaryBlockNames
);

export const selectDimensionalSummaryBlockExpandedStates = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) => reportConfig && reportConfig.presentationConfiguration.dimensionalSummaryBlockExpandedStates
);

export const selectDimensionalSummaryQueries = createSelector(selectCurrentReportConfiguration, (reportConfig) =>
  reportConfig ? reportConfig.dimensionalSummaryQueries : []
);

export const selectDimensionalQueryAccountType = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) =>
    reportConfig &&
    reportConfig.dimensionalSummaryQueries[0] &&
    reportConfig.dimensionalSummaryQueries[0].dimension.showAccountType
);

export const selectEntityFiscalYearStartMonth = createSelector(
  selectCurrentEntity,
  (entity) => entity.fiscal_year_start_month
);

export const selectExpandedGroupKeys = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig): IExpandedGroupKeys => reportConfig?.accountSummary?.expandedGroupKeys ?? {}
);

export const selectExpandedRows = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig): IExpandedRows => reportConfig?.accountSummary?.expandedRows ?? {}
);

export const selectFilterForColumn = createSelector(
  selectDetailTableFilter,
  (filters) => (columnName) => filters && filters.find((filter) => filter.columnName === columnName)
);

export const selectGroupByExpandedNodes = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) => reportConfig && reportConfig.presentationConfiguration.groupByExpandedNodes
);

export const selectHiddenControls = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) => reportConfig && reportConfig.visualizationQuery.hiddenControls
);

export const selectIsDimensionalQueryRunning = createSelector(
  selectIsQueryRunning,
  (isQueryRunning) =>
    isQueryRunning(EQueryType.dimensionalSummaryTableGroupData) ||
    isQueryRunning(EQueryType.dimensionalSummaryTableChildData)
);

export const selectIsInStandardReport = createSelector(
  selectCurrentPathname,
  selectPathname,
  selectCurrentEntity,
  (path, pathname, entity) =>
    path.indexOf(`${entity.subdomain}/standard/`) !== -1 || pathname.includes(`${entity.subdomain}/standard/`)
);

export const selectLegendSortOrder = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) => reportConfig && reportConfig.presentationConfiguration.legendSortOrder
);

export const selectStacking = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) => reportConfig?.presentationConfiguration.stacking ?? StackingOptions.Standard
);

export const selectOriginalDetailTableColumns = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) => reportConfig && reportConfig.detailTableQuery && reportConfig.detailTableQuery.columns
);

export const selectOrganizedBy = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) => reportConfig && reportConfig.visualizationQuery.organizedBy
);

export const selectReportConfigurationStatus = createSelector(
  reportConfigurationSelector,
  (state) => state.reportConfigurationStatus
);

export const selectRowGroups = createSelector(
  selectCurrentReportConfiguration,
  selectOrganizeColumnsByAccountTypeFlag,
  (state, selectOrganizeColumnsByAccountTypeFlag) => {
    if (selectOrganizeColumnsByAccountTypeFlag) {
      return (
        state?.accountSummary?.rowGroups.map((rowGroup) => {
          // Only check if showOnly has truthy value
          if (isRowDataSource(rowGroup) && Boolean(rowGroup.showOnly) && Array.isArray(rowGroup.showOnly.value)) {
            return {
              ...rowGroup,
              showOnly: {
                ...rowGroup.showOnly,
                value: EAllLedgerType.al
              }
            } as IRowSource;
          }
          return rowGroup;
        }) || []
      );
    }
    return state?.accountSummary?.rowGroups || [];
  }
);

export const selectOverrideRowGroups = createSelector(selectCurrentReportConfiguration, (state) => {
  const accountSummary = state?.accountSummary;
  if (!accountSummary || isMultiHierarchyAccountSummary(accountSummary)) {
    return [];
  }
  return accountSummary.rowGroups.filter(isRowOverride);
});

export const selectMultiHierarchyOverrideRowGroups = createSelector(
  selectCurrentReportConfiguration,
  // Annotated as nullable: the config is null before a report loads, so the optional chain is
  // required at runtime — typing the param `| null` keeps `no-unnecessary-condition` quiet.
  (state: IReportConfiguration | null) => {
    const accountSummary = state?.accountSummary;
    if (!accountSummary || !isMultiHierarchyAccountSummary(accountSummary)) {
      return [];
    }
    return accountSummary.rowGroups.filter(isMultiHierarchyRowOverride);
  }
);

export const selectShouldPersistReportConfigurations = createSelector(
  reportConfigurationSelector,
  selectIsInStandardReport,
  (state, isStandardReport) => state.shouldPersistReportConfigurations && !isStandardReport
);

export const selectShowColumn = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) => reportConfig && reportConfig.visualizationQuery.showColumn
);

export const selectShowColumnName = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) => reportConfig && reportConfig.visualizationQuery.showColumn.name
);

export const selectShowColumnType = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) => reportConfig && reportConfig.visualizationQuery.showColumn.type
);

export const selectTarget = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) => reportConfig && reportConfig.layers.target
);

export const selectHasTarget = createSelector(selectTarget, (target) => {
  return !!(target && target.elseStatus && target.conditions.length > 0);
});

export const selectViewFilters = createSelector(selectCurrentReportConfiguration, (state) => {
  const accountSummary = state?.accountSummary;
  // MH reports use a different filter type (MultiHierarchyCoaFilter) that is incompatible
  // with the column view filter UI, so we return an empty array for them.
  if (!accountSummary || isMultiHierarchyAccountSummary(accountSummary)) return [];
  return accountSummary.filters;
});

export const selectMultiHierarchyViewFilters = createSelector(
  selectCurrentReportConfiguration,
  (state): ReadonlyArray<MultiHierarchyCompositeFilter> => {
    const emptyMultiHierarchyFilters: ReadonlyArray<MultiHierarchyCompositeFilter> = [];
    const accountSummary = state?.accountSummary;
    if (!accountSummary || !isMultiHierarchyAccountSummary(accountSummary)) {
      return emptyMultiHierarchyFilters;
    }
    return (accountSummary.filters ?? emptyMultiHierarchyFilters) as ReadonlyArray<MultiHierarchyCompositeFilter>;
  }
);

export const selectAsOfDate = createSelector(
  selectCurrentReportConfiguration,
  (state) => state?.accountSummary?.asOfDate || ''
);

export const selectHasDeletedReferences = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) => reportConfig?.metadata?.valid ?? false
);

export const selectDeletedRowGroupings = createSelector(
  selectCurrentReportConfiguration,
  (reportConfig) => reportConfig?.metadata?.deletedRowGroupings ?? {}
);

export const selectFormattingZeroRows = createSelector(selectCurrentReportConfiguration, (reportConfig) => {
  if (!reportConfig || !reportConfig.accountSummary || !reportConfig.accountSummary.formatting.zeroNumbers)
    return false;
  return reportConfig.accountSummary.formatting.zeroNumbers[0].enabled;
});
